import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import CustomTextarea, { CustomInput } from '../Components/CustomFormElements';
import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import type {
  InformationPackageArtifactApi,
  InformationPackageUnitDetailApi,
} from '../Services/InformationPackageApiProviderTypes';

import Table from '../Components/Tables';
import InformationPackageFeaturesTable from '../Components/InformationPackageFeaturesTable';

interface InformationPackageUnitProps {
  canEdit?: boolean;
}

type UnitState = Pick<InformationPackageUnitDetailApi, 'name' | 'summary' | 'artifacts'>;

const artifactsColumns = [
  {
    name: 'Id',
    selector: (row: InformationPackageArtifactApi) => row.key,
    sortable: false,
  },
  {
    name: 'Type',
    selector: (row: InformationPackageArtifactApi) => row.artifact_type,
    sortable: false,
  },
  {
    name: 'Mime',
    selector: (row: InformationPackageArtifactApi) => row.mime,
    sortable: false,
  },
];

function InformationPackageUnitContent() {
  const [unit, setUnit] = useState<UnitState>({
    name: '',
    summary: '',
    artifacts: [],
  });
  const [unitLoading, setUnitLoading] = useState(false);

  const navigate = useNavigate();
  const { organizationId, informationPackageId, unitId } = useParams();
  const { showAlert } = useAlert();
  const { getInformationPackageUnitById } = useInformationPackageApi();

  const fetchUnit = async () => {
    setUnitLoading(true);

    try {
      const response = await getInformationPackageUnitById(unitId as string);

      setUnit({
        name: response.name,
        summary: response.summary ?? '',
        artifacts: response.artifacts,
      });
    } catch {
      showAlert('fail', 'Failed to load information package unit');
    } finally {
      setUnitLoading(false);
    }
  };

  useEffect(() => {
    fetchUnit();
  }, [unitId]);

  const handleArtifactRowClick = (row: InformationPackageArtifactApi) => {
    navigate(
      `/organizations/${organizationId}/information-package/${informationPackageId}/unit/${unitId}/artifact/${row.key}`,
    );
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/information-package/${informationPackageId}`);
  };

  if (unitLoading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">Unit</h2>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id="unit-name"
                label="Unit Name"
                placeholder="Enter unit name"
                type="text"
                value={unit.name}
                onChange={(id: string, value: string) => setUnit({ ...unit, name: value })}
                readOnly={true}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id="summary"
                label="Summary"
                name="summary"
                addedText={unit.summary ?? ''}
                onChange={(value: string) => setUnit({ ...unit, summary: value })}
                resizable={false}
                readOnly={true}
                height="200px"
              />
            </div>
          </div>

          <div className="input-block d-flex flex-column justify-between">
            <h2 className="mb-4">Artifacts</h2>
            <Table
              title=""
              columns={artifactsColumns}
              data={unit.artifacts}
              selectableRowsHighlight
              pagination={false}
              onRowClicked={handleArtifactRowClick}
            />
          </div>

          <InformationPackageFeaturesTable unitId={unitId as string} />

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={true}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InformationPackageUnit(_props: InformationPackageUnitProps) {
  return (
    <InformationPackageApiProvider>
      <InformationPackageUnitContent />
    </InformationPackageApiProvider>
  );
}

export default InformationPackageUnit;
